// ─── KPI formula utilities ────────────────────────────────────────────────────

export const calcHealthScore = ({ productionPct = 0, inventoryHealthPct = 100, fulfillmentPct = 100, crewAttendancePct = 100 }) =>
  Math.round((productionPct * 0.30) + (inventoryHealthPct * 0.25) + (fulfillmentPct * 0.25) + (crewAttendancePct * 0.20));

export const calcInventoryHealthPct = ({ criticalCount = 0, warningCount = 0, healthyCount = 0 }) => {
  const total = criticalCount + warningCount + healthyCount;
  if (total === 0) return 100;
  return Math.round(((healthyCount + warningCount * 0.5) / total) * 100);
};

export const calcFulfillmentPct = ({ fulfillableCount = 0, atRiskCount = 0 }) => {
  const total = fulfillableCount + atRiskCount;
  if (total === 0) return 100;
  return Math.round((fulfillableCount / total) * 100);
};

export const calcDaysRemaining = (currentStock, dailyConsumptionRate) => {
  if (!dailyConsumptionRate || dailyConsumptionRate === 0) return 999;
  return Math.floor(currentStock / dailyConsumptionRate);
};

export const getInventoryStatus = (daysRemaining, currentStock, minThreshold) => {
  if (currentStock <= 0) return 'stockout';
  if (daysRemaining <= 2 || currentStock < minThreshold * 0.5) return 'critical';
  if (daysRemaining <= 7 || currentStock < minThreshold) return 'warning';
  return 'healthy';
};

export const deriveInventorySummaryFromItems = (items = []) =>
  items.reduce(
    (acc, item) => {
      if (item.status === 'critical' || item.status === 'stockout') acc.criticalCount += 1;
      else if (item.status === 'warning') acc.warningCount += 1;
      else acc.healthyCount += 1;
      return acc;
    },
    { criticalCount: 0, warningCount: 0, healthyCount: 0 }
  );

export const mapChemicalToInventoryItem = (chemical) => {
  const current_stock = Number(chemical.current_stock || 0);
  const minimum_threshold = Number(chemical.min_stock_alert ?? chemical.minimum_stock_alert ?? 0);
  const days_remaining = 999;
  return {
    id: chemical.id,
    name: chemical.name,
    category: 'chemical',
    current_stock,
    minimum_threshold,
    unit: chemical.unit,
    daily_consumption_rate: 0,
    days_remaining,
    status: getInventoryStatus(days_remaining, current_stock, minimum_threshold),
  };
};

export const calcProductionPace = (currentAchieved, currentTarget, workdayStartHour = 9, workdayEndHour = 18) => {
  const now = new Date();
  const hourOfDay = now.getHours() + now.getMinutes() / 60;
  const workdayProgress = Math.max(0, Math.min(1, (hourOfDay - workdayStartHour) / (workdayEndHour - workdayStartHour)));
  const expectedAchieved = currentTarget * workdayProgress;
  const pacePct = workdayProgress > 0 ? (currentAchieved / expectedAchieved) * 100 : 100;
  const projectedEOD = workdayProgress > 0 ? Math.round(currentAchieved / workdayProgress) : 0;
  return { workdayProgress: workdayProgress * 100, expectedAchieved: Math.round(expectedAchieved), pacePct: Math.round(pacePct), projectedEOD };
};

// Derive order urgency bucket from due_date
export const getOrderUrgency = (dueDateStr) => {
  const daysUntilDue = Math.floor((new Date(dueDateStr) - new Date()) / 86_400_000);
  if (daysUntilDue < 0) return { bucket: 'overdue', daysUntilDue };
  if (daysUntilDue <= 2) return { bucket: 'critical', daysUntilDue };
  if (daysUntilDue <= 5) return { bucket: 'at_risk', daysUntilDue };
  return { bucket: 'on_track', daysUntilDue };
};

// Derive at-risk orders purely from upcoming_orders array
export const deriveAtRiskOrders = (upcomingOrders = []) =>
  upcomingOrders
    .filter((o) => {
      const { daysUntilDue } = getOrderUrgency(o.due_date);
      const fulfilled = String(o.status || '').toLowerCase() === 'fulfilled';
      return !fulfilled && daysUntilDue <= 3;
    })
    .map((o) => ({ ...o, ...getOrderUrgency(o.due_date) }));

// Derive inventory-like summary from dashboard stats
export const deriveInventorySummary = (stats = {}) => {
  const total = stats.totalReagents || 0;
  const lowStock = stats.lowStockItems || 0;
  const critical = lowStock;
  const warning = 0;
  const healthy = Math.max(0, total - lowStock);
  return { criticalCount: critical, warningCount: warning, healthyCount: healthy };
};

// Status colour classes (Tailwind)
export const STATUS_COLORS = {
  critical: {
    bg: 'bg-red-50 dark:bg-red-950',
    border: 'border-red-200 dark:border-red-800',
    text: 'text-red-700 dark:text-red-300',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    bar: 'bg-red-500',
    dot: 'bg-red-500',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-950',
    border: 'border-amber-200 dark:border-amber-800',
    text: 'text-amber-700 dark:text-amber-300',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
    bar: 'bg-amber-500',
    dot: 'bg-amber-500',
  },
  healthy: {
    bg: 'bg-emerald-50 dark:bg-emerald-950',
    border: 'border-emerald-200 dark:border-emerald-800',
    text: 'text-emerald-700 dark:text-emerald-300',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
    bar: 'bg-emerald-500',
    dot: 'bg-emerald-500',
  },
  stockout: {
    bg: 'bg-red-100 dark:bg-red-900',
    border: 'border-red-300 dark:border-red-700',
    text: 'text-red-800 dark:text-red-200',
    badge: 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200',
    bar: 'bg-red-700',
    dot: 'bg-red-700',
  },
  neutral: {
    bg: 'bg-slate-50 dark:bg-slate-900',
    border: 'border-slate-200 dark:border-slate-700',
    text: 'text-slate-700 dark:text-slate-300',
    badge: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    bar: 'bg-slate-400',
    dot: 'bg-slate-400',
  },
};

export const getStatusColor = (status) => STATUS_COLORS[status] || STATUS_COLORS.neutral;

export const getKpiStatus = (value, greenThreshold, yellowThreshold, inverse = false) => {
  if (inverse) {
    if (value <= greenThreshold) return 'healthy';
    if (value <= yellowThreshold) return 'warning';
    return 'critical';
  }
  if (value >= greenThreshold) return 'healthy';
  if (value >= yellowThreshold) return 'warning';
  return 'critical';
};
